'use client'

import type { ReactNode } from 'react'

import { useMutation } from '@tanstack/react-query'
import { usePathname } from 'next/navigation'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import axios from '@/_lib/axiosInstance'
import { pushSimpleEvent } from '@/_utils/analytics/datalayer'

/**
 * Newsletter form data structure.
 */
interface FormValues {
	email: string
	agreedToTermsOfService: boolean
	agreedToPersonalDataProtection: boolean
	agreedToPersonalDataProcessingForMarketingPurposes: boolean
	recaptchaToken?: string | null
}

/** Toast notification auto-close duration in milliseconds */
const TOAST_AUTO_CLOSE_DURATION = 5000

export interface ANewsletterFormClientProps {
	emailPlaceholder: string
	emailErrorMessage: string
	marketingConsentErrorMessage: string
	submitText: string
	toastSuccess: string
	toastErrorSubmit: string
	toastErrorCaptcha: string
	marketingConsentContent: ReactNode
}

/**
 * Interactive newsletter subscription form (client-only).
 */
const ANewsletterFormClient: React.FC<ANewsletterFormClientProps> = ({
	emailPlaceholder,
	emailErrorMessage,
	marketingConsentErrorMessage,
	submitText,
	toastSuccess,
	toastErrorSubmit,
	toastErrorCaptcha,
	marketingConsentContent
}) => {
	const pathname = usePathname()
	const { executeRecaptcha } = useGoogleReCaptcha()

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm<FormValues>()

	const mutation = useMutation({
		mutationFn: (data: FormValues) => {
			return axios.post('/v1/web/newsletter', data)
		},
		onSuccess: (response, variables) => {
			if (response.status === 201) {
				pushSimpleEvent('generate_lead', {
					form_name: 'newsletter',
					form_destination: 'mailing-list',
					form_location: pathname,
					value: 0,
					currency: 'EUR',
					user_data: {
						email: variables.email
					}
				})
			}
			reset()
			if (response.status === 201) {
				toast.success(toastSuccess, {
					position: 'top-right',
					className: '!bg-medante-pink-secondary',
					autoClose: TOAST_AUTO_CLOSE_DURATION,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true
				})
			}
		},
		onError: (error: unknown) => {
			const err = error as {
				response?: { data?: { error?: string } }
				message?: string
			}
			const errorMessage = err?.response?.data?.error || err?.message || ''
			const isCaptchaError =
				errorMessage.toLowerCase().includes('captcha') ||
				errorMessage.toLowerCase().includes('recaptcha')

			const message = isCaptchaError ? toastErrorCaptcha : toastErrorSubmit

			toast.error(message, {
				className: '!bg-medante-pink-secondary',
				position: 'top-right',
				autoClose: TOAST_AUTO_CLOSE_DURATION,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})
			console.error('Error submitting form:', error)
		}
	})

	const onSubmit = async (data: FormValues) => {
		if (!executeRecaptcha) {
			console.warn('reCAPTCHA not available')
			mutation.mutate({
				...data,
				recaptchaToken: null
			})
			return
		}

		try {
			const token = await executeRecaptcha('newsletter_submit')
			mutation.mutate({
				...data,
				recaptchaToken: token
			})
		} catch (error) {
			console.error('reCAPTCHA error:', error)
			mutation.mutate({
				...data,
				recaptchaToken: null
			})
		}
	}

	return (
		<div className='box_nl_form'>
			<form name='newsletter-form' onSubmit={handleSubmit(onSubmit)}>
				<div className='input_text_cont '>
					<input
						type='email'
						{...register('email', {
							required: emailErrorMessage,
							pattern: {
								value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
								message: emailErrorMessage
							}
						})}
						className='input_text'
						placeholder={emailPlaceholder}
						autoComplete='email'
					/>
					{errors.email && (
						<p className='mt-5 text-red-200 text-xs'>{errors.email.message}</p>
					)}
					<label className='input_text_placeholder'>{emailPlaceholder}</label>
				</div>

				<div className='pdt-15 pdt-5md'>
					<label className='input_checkbox_cont mgt-20'>
						<div className='checkbox-wrapper'>
							<input
								type='checkbox'
								className={`input_checkbox ${
									errors.agreedToPersonalDataProcessingForMarketingPurposes
										? 'error'
										: ''
								}`}
								{...register('agreedToPersonalDataProcessingForMarketingPurposes', {
									required: marketingConsentErrorMessage
								})}
							/>
							<div className='input_checkbox_text'>{marketingConsentContent}</div>
						</div>
						{errors.agreedToPersonalDataProcessingForMarketingPurposes && (
							<div className='text-red-200 text-xs mt-5'>
								{marketingConsentErrorMessage}
							</div>
						)}
					</label>
				</div>

				<div className='mgt-25 text_right_md text_center_mdm'>
					<button type='submit' className='btn_basic' disabled={mutation.isPending}>
						<span>{submitText}</span>
						<span>
							<span>
								<svg className='sico'>
									<use href='/assets/layout/icons.svg#s_ar2_r' />
								</svg>
							</span>
							<span>
								<svg className='sico'>
									<use href='/assets/layout/icons.svg#s_ar2_r' />
								</svg>
							</span>
						</span>
					</button>
				</div>
			</form>
		</div>
	)
}

export default ANewsletterFormClient
